import React, { useState, useEffect, useCallback } from 'react';
import Button from '@mui/material/Button';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Pagination from '@mui/material/Pagination';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import { FiSearch } from 'react-icons/fi';
import { AiOutlineClose } from 'react-icons/ai';
import { FaTag } from 'react-icons/fa';
import EditBrandModal from './EditBrandModal';
import BrandModal from './BrandModal';
import AxiosUrl from '../AxiosUrl';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import styles from './Brand.module.css';

function Brand() {
    const uid = localStorage.getItem('id');
    const [brands, setBrands] = useState([]);
    const [openEdit, setOpenEdit] = useState(false);
    const [openCreate, setOpenCreate] = useState(false);
    const [editData, setEditData] = useState('');
    const [refresh, setRefresh] = useState(0);
    const [loading, setLoading] = useState(true);

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [totalCount, setTotalCount] = useState(0);
    const brandsPerPage = 10;

    // Search and filter state
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [searchDebounce, setSearchDebounce] = useState(null);

    const fetchBrands = useCallback(
        async (page = 1, search = '', status = '') => {
            setLoading(true);
            try {
                const response = await AxiosUrl.get(`/search-brands/${uid}`, {
                    params: { searchQuery: search, status, page, limit: brandsPerPage },
                });
                if (response.data.success) {
                    setBrands(response.data.data);
                    setTotalPages(response.data.pagination.totalPages);
                    setTotalCount(response.data.pagination.totalCount);
                } else {
                    toast.error('Failed to fetch brands');
                }
            } catch (error) {
                console.error('Error fetching brands:', error);
                toast.error('Failed to load brands');
            } finally {
                setLoading(false);
            }
        },
        [uid]
    );

    useEffect(() => {
        if (!openEdit && !openCreate) {
            fetchBrands(currentPage, searchQuery, statusFilter);
        }
    }, [refresh, openEdit, openCreate, currentPage, statusFilter, searchQuery, fetchBrands]);

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchQuery(value);
        if (searchDebounce) clearTimeout(searchDebounce);
        const timeout = setTimeout(() => {
            setCurrentPage(1);
            fetchBrands(1, value, statusFilter);
        }, 300);
        setSearchDebounce(timeout);
    };

    const clearSearch = () => {
        setSearchQuery('');
        setCurrentPage(1);
        fetchBrands(1, '', statusFilter);
    };

    const handleStatusChange = (e) => {
        setStatusFilter(e.target.value);
        setCurrentPage(1);
    };

    const handlePageChange = (event, page) => setCurrentPage(page);

    const deleteBrand = (bid) => {
        const confirmed = window.confirm('Are you sure you want to delete this brand?');
        if (confirmed) {
            AxiosUrl.post(`/delete-brand/${uid}`, { bid })
                .then((res) => {
                    if (res.data.success || res.data.data?.acknowledged || res.data.acknowledged) {
                        toast.success('Deleted successfully.');
                        let pageToFetch = currentPage;
                        if (brands.length === 1 && currentPage > 1) {
                            pageToFetch = currentPage - 1;
                            setCurrentPage(pageToFetch);
                        }
                        setRefresh((prev) => prev + 1);
                        fetchBrands(pageToFetch, searchQuery, statusFilter);
                    } else if (res.data.error) {
                        toast.error(res.data.error);
                    } else {
                        toast.error('Failed to delete the brand.');
                    }
                })
                .catch((error) => {
                    const errorMessage = error.response?.data?.error || 'Failed to delete the brand.';
                    toast.error(errorMessage);
                });
        }
    };

    return (
        <>
            <ToastContainer />
            {openEdit && (
                <EditBrandModal
                    setOpenModal={setOpenEdit}
                    data={editData}
                    setRefresh={() => setRefresh((prev) => prev + 1)}
                />
            )}
            {openCreate && (
                <BrandModal
                    setOpenBrandModal={setOpenCreate}
                    open={openCreate}
                    setRefresh={() => setRefresh((prev) => prev + 1)}
                />
            )}

            <div style={{ minHeight: '90vh', padding: '0' }}>
                <div style={{ background: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                    {/* Header */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', gap: '16px', flexWrap: 'wrap' }}>
                        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flex: 1 }}>
                            <Button
                                variant="contained"
                                onClick={() => setOpenCreate(true)}
                                style={{ backgroundColor: '#008060', color: 'white', padding: '10px 20px', borderRadius: '8px', textTransform: 'none', fontWeight: '500', boxShadow: 'none', fontSize: '14px' }}
                            >
                                Add Brand
                            </Button>

                            {/* Search */}
                            <div style={{ flex: 1, maxWidth: '400px', display: 'flex', alignItems: 'center', gap: '12px', border: '1px solid #e5e7eb', padding: '8px 16px', backgroundColor: '#fff', borderRadius: '9999px' }}>
                                <input
                                    type="text"
                                    placeholder="Search brands..."
                                    value={searchQuery}
                                    onChange={handleSearchChange}
                                    style={{ width: '100%', border: 'none', outline: 'none', fontSize: '14px', color: '#374151' }}
                                />
                                {searchQuery !== '' ? (
                                    <AiOutlineClose onClick={clearSearch} style={{ fontSize: '18px', color: '#6b7280', cursor: 'pointer' }} />
                                ) : (
                                    <FiSearch style={{ fontSize: '18px', color: '#6b7280' }} />
                                )}
                            </div>

                            {/* Status Filter */}
                            <FormControl size="small" style={{ minWidth: '150px' }}>
                                <InputLabel>Status</InputLabel>
                                <Select value={statusFilter} onChange={handleStatusChange} label="Status" style={{ borderRadius: '8px' }}>
                                    <MenuItem value="">All</MenuItem>
                                    <MenuItem value="0">Active</MenuItem>
                                    <MenuItem value="1">Inactive</MenuItem>
                                </Select>
                            </FormControl>
                        </div>

                        <div style={{ fontSize: '14px', color: '#6d7175', fontWeight: '500' }}>
                            {totalCount} brands
                        </div>
                    </div>

                    {/* Table */}
                    {loading ? (
                        <div style={{ borderRadius: '12px', border: '1px solid #e5e7eb', overflow: 'hidden', background: 'white', marginBottom: '24px' }}>
                            {/* Skeleton header row */}
                            <div style={{ display: 'flex', alignItems: 'center', padding: '14px 16px', background: '#f8fafc', borderBottom: '1px solid #e5e7eb', gap: '16px' }}>
                                <div className={styles.skeleton} style={{ width: '60px', height: '14px' }}></div>
                                <div style={{ flex: 1 }}><div className={styles.skeleton} style={{ width: '60px', height: '14px' }}></div></div>
                                <div style={{ flex: 1 }}><div className={styles.skeleton} style={{ width: '70px', height: '14px' }}></div></div>
                                <div style={{ width: '100px', textAlign: 'center' }}><div className={styles.skeleton} style={{ width: '60px', height: '14px', margin: '0 auto' }}></div></div>
                                <div style={{ width: '160px', textAlign: 'center' }}><div className={styles.skeleton} style={{ width: '70px', height: '14px', margin: '0 auto' }}></div></div>
                            </div>
                            {/* Skeleton data rows */}
                            {[...Array(8)].map((_, i) => (
                                <div key={i} style={{ display: 'flex', alignItems: 'center', padding: '12px 16px', borderBottom: i < 7 ? '1px solid #f3f4f6' : 'none', gap: '16px' }}>
                                    <div className={styles.skeleton} style={{ width: '60px', height: '60px', borderRadius: '8px', flexShrink: 0 }}></div>
                                    <div style={{ flex: 1 }}><div className={styles.skeleton} style={{ width: `${100 + (i % 3) * 30}px`, height: '16px' }}></div></div>
                                    <div style={{ flex: 1 }}><div className={styles.skeleton} style={{ width: `${120 + (i % 2) * 40}px`, height: '14px' }}></div></div>
                                    <div style={{ width: '100px', textAlign: 'center' }}><div className={styles.skeleton} style={{ width: '64px', height: '24px', borderRadius: '12px', margin: '0 auto' }}></div></div>
                                    <div style={{ width: '160px', display: 'flex', gap: '8px', justifyContent: 'center' }}>
                                        <div className={styles.skeleton} style={{ width: '60px', height: '32px', borderRadius: '8px' }}></div>
                                        <div className={styles.skeleton} style={{ width: '70px', height: '32px', borderRadius: '8px' }}></div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <TableContainer component={Paper} style={{ borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5', marginBottom: '24px' }}>
                            <Table>
                                <TableHead style={{ background: '#f8fafc' }}>
                                    <TableRow>
                                        <TableCell style={{ fontWeight: '600' }}>Image</TableCell>
                                        <TableCell style={{ fontWeight: '600' }}>Name</TableCell>
                                        <TableCell style={{ fontWeight: '600' }}>Website</TableCell>
                                        <TableCell align="center" style={{ fontWeight: '600' }}>Status</TableCell>
                                        <TableCell align="center" style={{ fontWeight: '600' }}>Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {brands.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={5} align="center">
                                                <div style={{ padding: '60px 20px' }}>
                                                    <div style={{ fontSize: '48px' }}><FaTag /></div>
                                                    <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#202223', margin: '16px 0 8px 0' }}>No brands found</h3>
                                                    <p style={{ fontSize: '14px', color: '#6d7175', margin: 0 }}>
                                                        {searchQuery || statusFilter ? 'Try adjusting your search or filters' : 'Get started by adding your first brand'}
                                                    </p>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        brands.map((brand) => (
                                            <TableRow key={brand._id} sx={{ '&:hover': { backgroundColor: '#f9fafb' }, transition: 'background-color 0.15s ease' }}>
                                                <TableCell>
                                                    <img src={brand.imgUrl} height={60} width={60} alt={brand.name} style={{ borderRadius: '8px', objectFit: 'cover', border: '1px solid #e5e7eb' }} />
                                                </TableCell>
                                                <TableCell style={{ fontSize: '14px', fontWeight: '500', color: '#202223' }}>{brand.name}</TableCell>
                                                <TableCell style={{ fontSize: '13px', color: '#6b7280' }}>
                                                    {brand.website ? (
                                                        <a href={brand.website} target="_blank" rel="noopener noreferrer" style={{ color: '#008060' }}>
                                                            {brand.website.replace(/^https?:\/\//, '')}
                                                        </a>
                                                    ) : '—'}
                                                </TableCell>
                                                <TableCell align="center">
                                                    <span style={{
                                                        backgroundColor: brand.status === 1 ? '#fef2f2' : '#f0fdf4',
                                                        color: brand.status === 1 ? '#991b1b' : '#166534',
                                                        padding: '4px 12px', fontSize: '12px', fontWeight: '500', borderRadius: '12px', display: 'inline-block',
                                                        border: brand.status === 1 ? '1px solid #fecaca' : '1px solid #bbf7d0',
                                                    }}>
                                                        {brand.status === 1 ? 'Inactive' : 'Active'}
                                                    </span>
                                                </TableCell>
                                                <TableCell align="center">
                                                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                                                        <Button
                                                            variant="outlined" size="small"
                                                            style={{ backgroundColor: '#f0fdf4', color: '#166534', border: '1px solid #bbf7d0', padding: '6px 16px', fontSize: '13px', fontWeight: '500', borderRadius: '8px', textTransform: 'none', boxShadow: 'none' }}
                                                            onClick={() => { setEditData(brand); setOpenEdit(true); }}
                                                        >
                                                            Edit
                                                        </Button>
                                                        <Button
                                                            variant="outlined" size="small"
                                                            style={{ backgroundColor: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca', padding: '6px 16px', fontSize: '13px', fontWeight: '500', borderRadius: '8px', textTransform: 'none', boxShadow: 'none' }}
                                                            onClick={() => deleteBrand(brand._id)}
                                                        >
                                                            Delete
                                                        </Button>
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}

                    {/* Pagination */}
                    {!loading && totalPages > 1 && (
                        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '32px' }}>
                            <Pagination
                                count={totalPages}
                                page={currentPage}
                                onChange={handlePageChange}
                                color="primary"
                                showFirstButton
                                showLastButton
                                siblingCount={1}
                                boundaryCount={1}
                                sx={{
                                    '& .MuiPaginationItem-root': { borderRadius: '8px', fontWeight: '500' },
                                    '& .Mui-selected': { backgroundColor: '#008060 !important', color: 'white' },
                                }}
                            />
                        </div>
                    )}
                </div>
            </div>
        </>
    );
}

export default Brand;
